import React from 'react';
import { Helmet } from 'react-helmet-async';
import { FormattedMessage, useIntl } from 'react-intl';
import styled from 'styled-components';

import { Link } from '../StyledComponents';
import { useSsrContext } from '../ssrContext';

const Page = styled.section`
  min-height: 70vh;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 4rem 1.25rem;
`;

const Card = styled.div`
  width: 100%;
  max-width: 640px;
  background: ${({ theme }) => theme.colors.cardBg};
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: 16px;
  box-shadow: ${({ theme }) => theme.shadows.elevated};
  padding: 3rem 2.5rem;
  text-align: center;
`;

const Code = styled.div`
  font-size: 3rem;
  font-weight: 700;
  color: ${({ theme }) => theme.colors.textMuted};
  margin-bottom: 0.5rem;
`;

const Headline = styled.h1`
  margin: 0 0 1rem;
  font-size: 1.5rem;
`;

const Subheadline = styled.p`
  margin: 0 0 1.5rem;
  color: ${({ theme }) => theme.colors.textMuted};
  line-height: 1.5;
`;

const GonePage: React.FC = () => {
  const intl = useIntl();
  const { context: ssrContext } = useSsrContext();

  if (ssrContext) {
    (ssrContext as { status?: number; gone?: boolean }).status = 410;
    (ssrContext as { gone?: boolean }).gone = true;
  }

  const title = intl.formatMessage({
    id: 'gone.metaTitle',
    defaultMessage: '410 — Content removed',
  });
  const description = intl.formatMessage({
    id: 'gone.metaDescription',
    defaultMessage: 'This content has been permanently removed from the archive.',
  });

  return (
    <Page role="main" aria-labelledby="gone-title">
      <Helmet>
        <title>{title}</title>
        <meta name="description" content={description} />
        <meta name="robots" content="noindex,nofollow" />
      </Helmet>
      <Card>
        <Code aria-hidden="true">410</Code>
        <Headline id="gone-title">
          <FormattedMessage
            id="gone.headline"
            defaultMessage="This content has been permanently removed"
          />
        </Headline>
        <Subheadline>
          <FormattedMessage
            id="gone.subheadline"
            defaultMessage="This newspaper is not part of the History Worm archive. Search our collection of historical US newspapers instead."
          />
        </Subheadline>
        <Link to="/">
          <FormattedMessage id="gone.cta.home" defaultMessage="Return to homepage" />
        </Link>
      </Card>
    </Page>
  );
};

export default GonePage;
